import Phaser from 'phaser';
import { UI_EVENTS } from '../constants/Events';
import { AccessibleSprite } from '../components/accessibility/AccessibleSprite';

const OVER = '_over';
const DOWN = '_down';
const DISABLED = '_disabled';

/**
 * @typedef ButtonSimpleOptions
 * @property {number} [x=0] x position of button
 * @property {number} [y=0] y position of button
 * @property {string} [id=null] id for button
 * @property {string} [sheet='buttons'] spritesheet containing button graphic
 * @property {string} [costume=null] spritesheet framename of button graphic<br><br>By default button will also check for _over, _down and _disabled extensions on costume name for supporting button states.
 * @property {bool} [enabled=true] if the button should be enabled on start
 * @property {string} [ariaLabel=null] If set, button will attempt to register to accessibility using supplied aria label
 * @property {string} [ariaLabelID=null] If set, button will attempt to register to accessibility, checking copy for this id
 * @property {bool|number|number[]} [vibrate=true] If button should attempt to vibrate in input, can set to true for default or add [vibrate]{@link components.core.VibratePlugin} config
 * @property {object} [clickSFX=false] sfx to play when button is clicked { sprite, id }
 * @property {object} [vo=false] vo to play when button is clicked { sprite, id }
 * @property {object} [keymap=null] assigns a keyboard key to this button, triggering click event if pressed ( eg/ space for skip/continue buttons )
 *
 */

/**
 * @alias class.ButtonSimple
 * @description Ironically named ButtonSimple provides a default button you can use by passing in a config, saving boiler plate button creation.<br><br>
 * The button dispatches a 'click' event, passing itself through as a parameter.<br>
 * When using [GameHudPlugin]{@link components.plugin.GameHudPlugin} this button class is the default used
 *
 * @property {Phaser.Scene} scene The scene calling the button
 * @property {ButtonSimpleOptions} options The button config
 *
 */
class ButtonSimple extends AccessibleSprite {
  constructor(scene, opts) {
    // init
    const {
      x,
      y,
      id,
      sheet,
      costume,
      vo,
      enabled = true,
      ariaLabel = false,
      ariaLabelID = false,
      event = false,
      clickSFX = false,
      vibrate = true,
      disableStyle = 'alpha',
      ariaFollow = false,
    } = opts;

    super(scene, x, y, sheet || 'buttons', costume);

    // button props
    this._clickSFX = clickSFX;
    this.id = id;
    this.costume = costume;
    this._vo = vo;
    this._opts = opts;
    this._keymap = opts.keymap;
    this._disableStyle = disableStyle;

    this.ariaLabel = ariaLabel
      ? ariaLabel
      : ariaLabelID
      ? this.scene.game.copy.get(ariaLabelID)
      : false;
    this.ariaFollow = ariaFollow;
    this.event = event;
    this._vibrate = vibrate;
    this._storedEnabled = null;
    this.buttonEnabled = null;

    // binded button events
    this.bindedEnterButtonHoverState = this.enterButtonHoverState.bind(this);
    this.bindedEnterButtonRestState = this.enterButtonRestState.bind(this);
    this.bindedEnterButtonActiveState = this.enterButtonActiveState.bind(this);
    this.bindedEnterButtonClickState = this.enterButtonClickState.bind(this);

    // start
    scene.add.existing(this);
    if (enabled) {
      this.enable();
      this.enterButtonRestState();
    } else {
      this.disable();
    }

    if (scene && scene.addButton) {
      scene.addButton(this);
    }
  }

  /**
   * @deprecated use syncAccessibilityElement instead for consistency
   */
  _updateAccessibilityItem() {
    this.syncAccessibilityElement();
  }

  set visible(value) {
    if (value && !this.visible) {
      if (this._storedEnabled === true) {
        this.enable();
      }
    } else if (!value && this.visible) {
      this._storedEnabled = this.buttonEnabled;
      this.disable();
    }
    super.visible = value;
  }

  get visible() {
    return super.visible;
  }

  /**
   * Enable the button
   */
  enable() {
    if (this.buttonEnabled) return;

    if (this._disableStyle == 'alpha') {
      this.alpha = 1;
    }
    this.buttonEnabled = true;
    this.setDisplay(this.costume);
    this.enableInteractive();
    this.on('pointerover', this.bindedEnterButtonHoverState);
    this.on('pointerout', this.bindedEnterButtonRestState);
    this.on('pointerdown', this.bindedEnterButtonActiveState);
    this.on('pointerup', this.bindedEnterButtonClickState);
    if (this._keymap)
      this.scene.input.keyboard.on('keydown-' + this._keymap, this.enterButtonClickState, this);
  }

  enableInteractive() {
    this.setInteractive({ useHandCursor: true });
  }

  /**
   * Disable the button
   */
  disable() {
    if (!this.buttonEnabled) return;

    this.buttonEnabled = false;
    if (this._disableStyle == 'alpha') {
      this.alpha = 0.25;
    }
    this.setDisplay(this.costume + DISABLED);
    this.disableInteractive();
    this.off('pointerover', this.bindedEnterButtonHoverState);
    this.off('pointerout', this.bindedEnterButtonRestState);
    this.off('pointerdown', this.bindedEnterButtonActiveState);
    this.off('pointerup', this.bindedEnterButtonClickState);
    if (this._keymap)
      this.scene.input.keyboard.off('keydown-' + this._keymap, this.enterButtonClickState, this);
  }

  // button events
  enterButtonHoverState() {
    if (this.buttonEnabled) {
      if (this._vo) {
        this.scene.game.soundController.playButtonAudio(this._vo.sprite ?? this._vo.sheet, this._vo.id);
      }
      this.setDisplay(this.costume + OVER);
      if (this.scaleOnHover) {
        this._hoverScale = this.scale + this.scaleOnHover;
        this.scale = this._hoverScale;
      }
    }
  }

  enterButtonRestState() {
    if (this._hoverScale) this.scale = this._hoverScale - this.scaleOnHover;
    this.setDisplay(this.costume);
  }

  enterButtonActiveState() {
    if (this.scaleOnHover) {
      this._hoverScale = this.scale + this.scaleOnHover;
      this.scale = this._hoverScale;
    }
    this.setDisplay(this.costume + DOWN);
  }

  enterButtonClickState() {
    if (this._clickSFX) this._playSFX(this._clickSFX);
    if (this._vibrate && this.scene.vibrate) {
      this.scene.vibrate.trigger(this._vibrate === true ? 200 : this._vibrate);
    }
    this.setDisplay(this.costume);
    this.emit(UI_EVENTS.CLICK, this);
  }

  setDisplay(frame) {
    if (this.texture.has(frame)) {
      this.setTexture(this.texture.key, frame);
    }
  }

  _playSFX(sfx) {
    this.scene.game.soundController.playSFX(sfx.sprite, sfx.id, { volume: sfx.volume || 1 });
  }

  // =============================================================================================
  // ACCESSIBILITY
  // =============================================================================================

  _handleAccessibilityFocus() {
    super._handleAccessibilityFocus();
    this.enterButtonHoverState();
  }

  _handleAccessibilityBlur() {
    super._handleAccessibilityBlur();
    this.enterButtonRestState();
  }

  _handleAccessibilitySelect() {
    super._handleAccessibilitySelect();
    this.enterButtonClickState();
  }

  // =============================================================================================
  // CLEAN UP
  // =============================================================================================

  destroy(fromScene = true) {
    this.disable();
    if (this.scene && this.scene.removeButton) {
      this.scene?.removeButton(this);
    }
    super.destroy(fromScene);
  }
}

export { ButtonSimple };
